import { supabase } from '@/lib/supabase/client';
import { getRecallClusters } from '@/services/spatialClustering';
import { tenantAPI } from '@/services/api/tenants';

/**
 * Comprehensive debug utility to investigate zone-recall mapping issues
 */

export async function debugZoneRecallMapping() {
  console.log('🔍 Debugging Zone-Recall Mapping...');

  try {
    // 1. Check all zones and their status
    console.log('📊 Step 1: Checking zones and their status...');

    const allZones = await tenantAPI.getAllZonesWithZipCounts();
    console.log(`✅ Found ${allZones.length} total zones`);

    const georgiaZones = allZones.filter(
      zone =>
        zone.region?.toLowerCase().includes('ga') ||
        zone.zone_name?.toLowerCase().includes('georgia') ||
        zone.zone_name?.toLowerCase().includes('atlanta')
    );

    console.log(`🍑 Found ${georgiaZones.length} Georgia-related zones:`);
    georgiaZones.forEach(zone => {
      console.log(`  - ${zone.zone_name} (${zone.status}) - ${zone.zip_code_count} zip codes`);
    });

    // 2. Check active zones specifically
    const activeZones = allZones.filter(zone => zone.status === 'active');
    console.log(`✅ Found ${activeZones.length} active zones (these are what the map shows)`);

    const activeGeorgiaZones = activeZones.filter(
      zone =>
        zone.region?.toLowerCase().includes('ga') ||
        zone.zone_name?.toLowerCase().includes('georgia') ||
        zone.zone_name?.toLowerCase().includes('atlanta')
    );

    console.log(`🍑 Found ${activeGeorgiaZones.length} active Georgia zones`);

    // 3. For each active Georgia zone, check ZIP codes and recall data
    for (const zone of activeGeorgiaZones) {
      console.log(`\n🔍 Checking zone: ${zone.zone_name}`);

      try {
        const zipCodes = await tenantAPI.getZoneZipCodes(zone.id);
        console.log(`  📮 ZIP codes: ${zipCodes.length} found`);

        if (zipCodes.length > 0) {
          console.log(`  📮 Sample ZIPs: ${zipCodes.slice(0, 5).join(', ')}`);

          // Check if these ZIP codes have recall data
          const { data: zipRecallData, error: zipError } = await supabase
            .from('cars_with_safety_recalls_mv')
            .select('seller_postal_code, service_provider_name, vin')
            .in('seller_postal_code', zipCodes.slice(0, 10)) // Check first 10 ZIP codes
            .limit(20);

          if (zipError) {
            console.error(`  ❌ Error checking ZIP recall data: ${zipError.message}`);
          } else {
            console.log(
              `  📊 Found ${zipRecallData?.length || 0} vehicles in this zone's ZIP codes`
            );
            if (zipRecallData && zipRecallData.length > 0) {
              const uniqueZips = [...new Set(zipRecallData.map(r => r.seller_postal_code))];
              console.log(`  📮 ZIPs with vehicles: ${uniqueZips.join(', ')}`);
            }
          }
        }
      } catch (error) {
        console.error(`  ❌ Error checking zone ${zone.zone_name}: ${error}`);
      }
    }

    // 4. Check if there are recalls in Georgia that aren't covered by zones
    console.log('\n📊 Step 4: Checking Georgia recalls not covered by active zones...');

    const allActiveZipCodes = new Set<string>();
    for (const zone of activeGeorgiaZones) {
      try {
        const zipCodes = await tenantAPI.getZoneZipCodes(zone.id);
        zipCodes.forEach(zip => allActiveZipCodes.add(zip));
      } catch (error) {
        console.error(`Error getting ZIP codes for ${zone.zone_name}:`, error);
      }
    }

    console.log(`📮 Total active zone ZIP codes in Georgia: ${allActiveZipCodes.size}`);

    // Check recalls in Georgia not covered by active zones
    const { data: uncoveredRecalls, error: uncoveredError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select('seller_postal_code, service_provider_name, seller_city, vin')
      .eq('seller_region', 'GA')
      .not(
        'seller_postal_code',
        'in',
        `(${Array.from(allActiveZipCodes)
          .map(z => `'${z}'`)
          .join(',')})`
      )
      .limit(50);

    if (uncoveredError) {
      console.error('❌ Error checking uncovered recalls:', uncoveredError.message);
    } else {
      console.log(
        `🚨 Found ${uncoveredRecalls?.length || 0} vehicles in Georgia ZIPs NOT covered by active zones`
      );
      if (uncoveredRecalls && uncoveredRecalls.length > 0) {
        const uncoveredZips = [...new Set(uncoveredRecalls.map(r => r.seller_postal_code))];
        console.log(`📮 Uncovered ZIPs: ${uncoveredZips.slice(0, 10).join(', ')}`);
        console.log(
          `🏪 Sample dealers: ${uncoveredRecalls
            .slice(0, 5)
            .map(r => `${r.service_provider_name} (${r.seller_city})`)
            .join(', ')}`
        );
      }
    }

    // 5. Check coordinate bounds for Georgia zones
    console.log('\n🗺️ Step 5: Checking coordinate bounds for Georgia zones...');

    for (const zone of activeGeorgiaZones) {
      try {
        // Get zone geometry if available
        const { data: zoneGeometry, error: geoError } = await supabase
          .from('zones')
          .select('geometry, metadata')
          .eq('id', zone.id)
          .single();

        if (geoError) {
          console.error(`  ❌ Error getting geometry for ${zone.zone_name}: ${geoError.message}`);
        } else if (zoneGeometry?.geometry) {
          console.log(`  🗺️ ${zone.zone_name} has geometry defined`);

          // Check if the geometry intersects with GA bounds
          const bounds = calculateBounds(zoneGeometry.geometry);
          if (bounds) {
            console.log(
              `    Bounds: ${bounds.north}, ${bounds.south}, ${bounds.east}, ${bounds.west}`
            );

            // Test if this intersects with Georgia bounds
            const georgiaIntersection = boundsIntersectGeorgia(bounds);
            console.log(`    Intersects Georgia: ${georgiaIntersection}`);
          }
        } else {
          console.log(`  📍 ${zone.zone_name} has no geometry (ZIP-based only)`);
        }
      } catch (error) {
        console.error(`  ❌ Error checking geometry for ${zone.zone_name}: ${error}`);
      }
    }

    return {
      totalZones: allZones.length,
      activeZones: activeZones.length,
      georgiaZones: georgiaZones.length,
      activeGeorgiaZones: activeGeorgiaZones.length,
      activeGeorgiaZipCodes: allActiveZipCodes.size,
      uncoveredVehicles: uncoveredRecalls?.length || 0
    };
  } catch (error) {
    console.error('❌ Debug failed:', error);
    return null;
  }
}

// Helper functions
function calculateBounds(geometry: any) {
  try {
    if (geometry.type === 'Polygon' && geometry.coordinates?.[0]) {
      const coords = geometry.coordinates[0];
      let north = -90,
        south = 90,
        east = -180,
        west = 180;

      coords.forEach(([lng, lat]: [number, number]) => {
        if (lat > north) north = lat;
        if (lat < south) south = lat;
        if (lng > east) east = lng;
        if (lng < west) west = lng;
      });

      return { north, south, east, west };
    }
    return null;
  } catch (error) {
    console.error('Error calculating bounds:', error);
    return null;
  }
}

function boundsIntersectGeorgia(bounds: {
  north: number;
  south: number;
  east: number;
  west: number;
}) {
  const GA_BOUNDS = {
    north: 35.0, // Northern border
    south: 30.3, // Southern border
    east: -80.8, // Eastern border
    west: -85.6 // Western border
  };

  return !(
    bounds.south > GA_BOUNDS.north ||
    bounds.north < GA_BOUNDS.south ||
    bounds.west > GA_BOUNDS.east ||
    bounds.east < GA_BOUNDS.west
  );
}

// Export to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugZoneRecallMapping = debugZoneRecallMapping;
}

export default debugZoneRecallMapping;
